import React, { useEffect, useRef, useState } from 'react';
import ShareButton from '../ui/ShareButton';
import ChartExportButton from '../ui/ChartExportButton';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import './matchScorecard.css';
import { apiErrorText } from '../../utils/apiError';

const SCREEN_OPTIONS = [
  { key: 'summary', label: 'Summary' },
  { key: 'batting', label: 'Batting' },
  { key: 'bowling', label: 'Bowling' },
];

const BAT_LENSES = [
  { key: 'bowler', label: 'vs Bowler' },
  { key: 'phase', label: 'Phase' },
  { key: 'pace', label: 'Pace / Spin' },
  { key: 'zones', label: 'Zones' },
  { key: 'grid', label: 'Line.Len' },
];

const BOWL_LENSES = [
  { key: 'batter', label: 'vs Batter' },
  { key: 'phase', label: 'Phase' },
  { key: 'hand', label: 'RH / LH' },
  { key: 'zones', label: 'Zones' },
  { key: 'grid', label: 'Line.Len' },
];

const getAccent = (data, team, fallback = '#f0b429') => (
  data?.match?.teams?.find((item) => item.name === team)?.accent || fallback
);

const getScreen = (params) => {
  const value = params.get('screen');
  return ['summary', 'batting', 'bowling'].includes(value) ? value : 'summary';
};

const getNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const setParam = (searchParams, setSearchParams, updates) => {
  const next = new URLSearchParams(searchParams);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
  });
  setSearchParams(next);
};

const scrollToTarget = (id) => {
  if (!id) return;
  requestAnimationFrame(() => {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 40,
        behavior: 'smooth',
      });
    }, 80);
  });
};

const MatchScorecardPage = () => {
  const { matchId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const targetHandledRef = useRef('');
  const minBalls = searchParams.get('min_balls') || 6;

  useEffect(() => {
    let cancelled = false;
    const fetchScorecard = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${config.API_URL}/matches/${matchId}/scorecard`, {
          params: { min_balls: minBalls },
        });
        if (!cancelled) setData(response.data);
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorText(err, null) || err.message || 'Failed to load scorecard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchScorecard();
    return () => {
      cancelled = true;
    };
  }, [matchId, minBalls]);

  const screen = getScreen(searchParams);
  const inningsNumber = getNumber(searchParams.get('innings'), data?.innings?.[0]?.innings || 1);
  const innings = data?.innings?.find((item) => item.innings === inningsNumber) || data?.innings?.[0];
  const target = searchParams.get('target');

  useEffect(() => {
    if (!data || !target || targetHandledRef.current === target) return;
    targetHandledRef.current = target;
    scrollToTarget(target);
  }, [data, target, screen, inningsNumber]);

  if (loading) {
    return <div className="scorecard-page"><div className="scorecard-shell"><div className="scorecard-loading">Loading scorecard...</div></div></div>;
  }

  if (error || !data) {
    return <div className="scorecard-page"><div className="scorecard-shell"><div className="scorecard-error">{error || 'Scorecard unavailable'}</div></div></div>;
  }

  return (
    <div className="scorecard-page">
      <div className={`scorecard-shell${screen === 'summary' ? ' is-summary' : ''}`}>
        <ScorecardTopNav
          data={data}
          screen={screen}
          setScreen={(nextScreen) => setParam(searchParams, setSearchParams, { screen: nextScreen, target: null })}
        />

        {screen !== 'summary' && (
          <InningsPicker
            innings={data.innings}
            active={innings?.innings}
            onChange={(nextInnings) => setParam(searchParams, setSearchParams, {
              innings: nextInnings,
              openBatter: null,
              openBowler: null,
              target: null,
            })}
          />
        )}

        {screen === 'summary' && (
          <ScorecardSummaryScreen
            data={data}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        )}
        {screen === 'batting' && innings && (
          <BattingBreakdownScreen
            data={data}
            innings={innings}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        )}
        {screen === 'bowling' && innings && (
          <BowlingBreakdownScreen
            data={data}
            innings={innings}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        )}
      </div>
    </div>
  );
};

const ScorecardTopNav = ({ data, screen, setScreen }) => (
  <header className="scorecard-top">
    <div>
      <div className="scorecard-kicker">{data.match.competition || data.match.event_name || 'Match Scorecard'}</div>
      <h1>{data.match.team1} vs {data.match.team2}</h1>
      <p>{data.match.venue}{data.match.city ? `, ${data.match.city}` : ''}</p>
    </div>
    <ShareButton
      kind="scorecard"
      title={`${data.match.team1} v ${data.match.team2}`}
      text={data.match.result_text}
      sx={{ alignSelf: 'flex-start' }}
    />
    <div className="scorecard-screen-tabs" role="tablist">
      {SCREEN_OPTIONS.map((option) => (
        <button
          key={option.key}
          className={screen === option.key ? 'active' : ''}
          onClick={() => setScreen(option.key)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  </header>
);

const InningsPicker = ({ innings, active, onChange }) => (
  <div className="innings-picker">
    {innings.map((item) => (
      <button
        key={item.innings}
        type="button"
        className={active === item.innings ? 'active' : ''}
        onClick={() => onChange(item.innings)}
      >
        <span>{item.batting_team}</span>
        <b>{item.score.runs}/{item.score.wickets}</b>
      </button>
    ))}
  </div>
);

const ScorecardSummaryScreen = ({ data, searchParams, setSearchParams }) => {
  const [fullOpen, setFullOpen] = useState(false);
  const winnerAccent = getAccent(data, data.match.winner);
  const winnerPrefix = data.match.winner ? `${data.match.winner} won by ` : '';
  const hasWinnerMargin = winnerPrefix && data.match.result_text?.startsWith(winnerPrefix);
  const openBattingInnings = (innings) => setParam(searchParams, setSearchParams, {
    screen: 'batting',
    innings,
    openBatter: null,
    openBowler: null,
    target: null,
  });
  const openPerformer = (item) => {
    if (!item.screen || !item.innings || !item.player_id) return;
    const isBatting = item.screen === 'batting';
    setParam(searchParams, setSearchParams, {
      screen: item.screen,
      innings: item.innings,
      batLens: isBatting ? item.lens : null,
      bowlLens: isBatting ? null : item.lens,
      openBatter: isBatting ? item.player_id : null,
      openBowler: isBatting ? null : item.player_id,
      target: `${isBatting ? 'batter' : 'bowler'}-${item.innings}-${item.player_id}`,
    });
  };

  return (
    <main className="scorecard-summary">
      <section className="result-card">
        <div className="scorecard-kicker">Result {data.match.venue ? `- ${data.match.venue}` : ''}</div>
        <h2>{hasWinnerMargin ? (
          <>
            {data.match.winner} won by <span style={{ color: winnerAccent }}>{data.match.result_text.replace(winnerPrefix, '')}</span>
          </>
        ) : data.match.result_text}</h2>
        {data.match.chase_note && <p>{data.match.chase_note}</p>}
        <div className="score-lines">
          {data.summary.innings_scores.map((score) => (
            <button
              type="button"
              className="score-line score-line-button"
              key={score.innings}
              style={{ '--team-accent': getAccent(data, score.team) }}
              onClick={() => openBattingInnings(score.innings)}
            >
              <span className="team-dot" />
              <span>{score.team}</span>
              <b>{score.runs}<small>/{score.wickets}</small></b>
              <em>{score.overs}</em>
            </button>
          ))}
        </div>
      </section>

      {/* One column of story cards: on desktop the full scorecard sits beside it, and as separate
          grid items these were stretched apart to match its height. */}
      <div className="summary-story">
        {data.summary.primer && <RecapCard matchId={data.match.id} />}
        <TopPerformers data={data} onSelect={openPerformer} />
        {data.match.player_of_match && <PlayerOfMatchCard player={data.match.player_of_match} />}
        <MomentumWorm data={data} />
        {data.summary.primer && <ImpactCard data={data} primer={data.summary.primer} />}
        <MoreScorecards match={data.match} />
      </div>

      <section className="scorecard-collapse">
        <button type="button" onClick={() => setFullOpen((value) => !value)}>
          <span>{fullOpen ? 'Hide full scorecard' : 'View full scorecard'}</span>
          <b>tap</b>
        </button>
        <div className={`full-scorecard ${fullOpen ? 'open' : ''}`}>
          {data.innings.map((item) => (
            <FullInnings key={item.innings} data={data} innings={item} />
          ))}
        </div>
      </section>
    </main>
  );
};

// Ways out to other scorecards (the /scorecards browse page, filtered): the same two sides, the same competition and
// season, and the same calendar day in other years. Also internal links for search engines.
const MoreScorecards = ({ match }) => {
  if (!match?.team1 || !match?.team2) return null;
  const q = (params) => `/scorecards?${new URLSearchParams(params).toString()}`;
  const year = match.date ? match.date.slice(0, 4) : null;
  const day = match.date ? match.date.slice(5, 10) : null;
  const dayLabel = day ? new Date(`2000-${day}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : null;
  const links = [
    [`Every ${match.team1} v ${match.team2} scorecard`, q({ team: match.team1, opponent: match.team2 })],
    match.competition && year ? [`${match.competition} ${year}`, q({ competition: match.competition, year })] : null,
    day ? [`On this day: ${dayLabel}`, q({ on: day })] : null,
  ].filter(Boolean);
  return (
    <section className="worm-card more-scorecards">
      <div className="card-heading-row"><span>More scorecards</span></div>
      {links.map(([label, to]) => (
        <Link key={to} to={to} className="more-scorecards-link">
          <span>{label}</span><b>→</b>
        </Link>
      ))}
    </section>
  );
};

// How the match was won: Impact/WPA facts written by the API, ranked by Jev (T20 Primer matches).
// Loaded separately so the scorecard itself never waits on it.
// Share image and embed for a scorecard card (recap / win probability), as a quiet footer action.
const CardExport = ({ kind, matchId }) => (
  <div className="card-export">
    <ChartExportButton
      request={{ kind, params: { match_id: String(matchId) } }}
      sx={{ color: '#c3c8d0', borderColor: 'rgba(255,255,255,.14)', fontSize: 12, textTransform: 'none' }}
    />
  </div>
);

const RecapCard = ({ matchId }) => {
  const [recap, setRecap] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${config.API_URL}/matches/${encodeURIComponent(matchId)}/recap`)
      .then((response) => { if (!cancelled) setRecap(response.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [matchId]);
  if (!recap?.available) return null;
  return (
    <section className="worm-card recap-card">
      <div className="card-heading-row"><span>How it was won</span></div>
      <p className="recap-headline">{recap.headline}</p>
      <ul className="recap-bullets">
        {recap.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
      </ul>
      <CardExport kind="recap" matchId={matchId} />
    </section>
  );
};

// "Kolkata Knight Riders" -> "KKR" for tight labels; short names pass through ("India").
const shortTeam = (name) => {
  if (!name) return '';
  const words = name.split(/\s+/).filter(Boolean);
  return name.length > 12 && words.length > 1 ? words.map((w) => w[0]).join('').toUpperCase() : name;
};

const signed = (value, digits = 1) => (value > 0 ? '+' : value < 0 ? '−' : '') + Math.abs(value).toFixed(digits);

// Win probability and Impact (Ganjoo's T20 Primer; the layout follows its "Impact scorecard").
// Win probability is drawn from the side batting first; Impact is runs added to the batting
// side's projected total, summed by over.
const ImpactCard = ({ data, primer }) => {
  const wp = primer.win_probability;
  const n = Math.max(1, wp.points.length - 1);
  const toX = (i) => (i / n) * 100;
  const toY = (p) => 58 - p * 56;
  const line = wp.points.map((p, i) => `${toX(i).toFixed(2)},${toY(p).toFixed(2)}`).join(' ');
  const firstTeam = wp.team;
  const secondTeam = primer.innings.find((inn) => inn.team !== firstTeam)?.team;
  const accent = getAccent(data, firstTeam);
  const maxOver = Math.max(1, ...primer.innings.flatMap((inn) => inn.by_over.map((o) => Math.abs(o.impact))));

  return (
    <section className="worm-card impact-card">
      <div className="card-heading-row">
        <span>Win probability · Impact</span>
        <div>
          {primer.innings.map((inn) => (
            <em key={inn.innings} style={{ color: getAccent(data, inn.team) }} title={inn.team}>
              {shortTeam(inn.team)} {signed(inn.impact)}
            </em>
          ))}
        </div>
      </div>
      <div className="wp-labels"><span>{shortTeam(firstTeam)} 100%</span><span>{shortTeam(secondTeam)} 100%</span></div>
      <svg viewBox="0 0 100 60" preserveAspectRatio="none" width="100%" height="140" aria-label={`Win probability for ${firstTeam}`}>
        <line x1="0" y1={toY(0.5)} x2="100" y2={toY(0.5)} className="mid" />
        <line x1={toX(wp.innings_break)} y1="0" x2={toX(wp.innings_break)} y2="60" className="break" />
        <polyline points={line} fill="none" stroke={accent} strokeWidth="2.2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {wp.wickets.map((i) => (
          <circle key={i} cx={toX(i)} cy={toY(wp.points[i])} r="1.1" className="wicket" />
        ))}
      </svg>
      <div className="impact-overs">
        {primer.innings.map((inn) => (
          <div key={inn.innings} className="impact-over-row">
            <small style={{ color: getAccent(data, inn.team) }} title={inn.team}>{shortTeam(inn.team)}</small>
            <div className="impact-bars" aria-label={`${inn.team} Impact by over`}>
              {inn.by_over.map((o) => (
                <span
                  key={o.over}
                  title={`Over ${o.over}: ${signed(o.impact, 2)}`}
                  className={o.impact >= 0 ? 'up' : 'down'}
                  style={{ height: `${Math.max(4, (Math.abs(o.impact) / maxOver) * 50)}%` }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="impact-note">
        Impact: runs added to the batting side&apos;s projected total, per over. Dots mark wickets.
      </p>
      <CardExport kind="win_prob" matchId={data.match.id} />
    </section>
  );
};

// Tick labels placed at their over, on the same scale as the worm (over / last tick).
const WormAxis = ({ ticks }) => {
  const last = ticks[ticks.length - 1] || 1;
  return (
    <div className="worm-axis is-positioned">
      {ticks.map((over, index) => (
        <span
          key={over}
          style={{
            left: `${(over / last) * 100}%`,
            transform: over === last ? 'translateX(-100%)' : 'translateX(-50%)',
          }}
        >
          {index === 0 ? `Ov ${over}` : over}
        </span>
      ))}
    </div>
  );
};

const MomentumWorm = ({ data }) => (
  <section className="worm-card">
    <div className="card-heading-row">
      <span>Momentum</span>
      <div>
        {data.summary.worm.map((line) => (
          <em key={line.innings} style={{ color: getAccent(data, line.team) }}>{line.team}</em>
        ))}
      </div>
    </div>
    <svg viewBox="0 0 100 60" preserveAspectRatio="none" width="100%" height="168">
      <line x1="0" y1="4" x2="100" y2="4" />
      <line x1="0" y1="32" x2="100" y2="32" />
      <line x1="0" y1="60" x2="100" y2="60" className="base" />
      {data.summary.worm.map((line) => (
        <polyline
          key={line.innings}
          points={line.points}
          fill="none"
          stroke={getAccent(data, line.team)}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
    <WormAxis ticks={data.summary.worm_axis || [5, 10, 15, 20]} />
  </section>
);

const PlayerOfMatchCard = ({ player }) => (
  <section className="pom-card">
    <div className="photo-block">PHOTO</div>
    <div>
      <div className="scorecard-kicker gold">Player of the match</div>
      <h2>{player || 'Unavailable'}</h2>
      <p>Official match award</p>
    </div>
  </section>
);

const TopPerformers = ({ data, onSelect }) => (
  <section className="top-performers">
    <div className="section-label">Top performers</div>
    <div className="performer-grid">
      {data.summary.top_performers.map((item, index) => (
        <button
          type="button"
          className="performer-card"
          key={`${item.kind}-${item.team}-${item.player}`}
          style={{ '--team-accent': getAccent(data, item.team, index % 2 ? '#5b8def' : '#f0b429') }}
          onClick={() => onSelect(item)}
        >
          <span>{item.kind === 'top_bat' ? 'TOP BAT' : 'TOP WKT'} - {item.team}</span>
          <b>{item.player}</b>
          <strong>{item.label}</strong>
        </button>
      ))}
    </div>
  </section>
);

const hasImpact = (innings) => innings.batting.some((row) => row.impact != null);
const impactClass = (value) => (value == null ? '' : value >= 0 ? 'impact-pos' : 'impact-neg');

const FullInnings = ({ data, innings }) => (
  <section className="full-innings">
    <div className="full-innings-title">
      <b>{innings.batting_team} <span style={{ color: getAccent(data, innings.batting_team) }}>{innings.score.runs}/{innings.score.wickets}</span></b>
      <span>RR {innings.score.run_rate}</span>
    </div>
    <div className={`mini-table header${hasImpact(innings) ? ' with-impact' : ''}`}>
      <span>BATTER</span><span>R</span><span>B</span><span>4s</span><span>6s</span>{hasImpact(innings) && <span>IMP</span>}
    </div>
    {innings.batting.map((row) => (
      <div className={`mini-table${hasImpact(innings) ? ' with-impact' : ''}`} key={row.id}>
        <span><b>{row.name}</b><small>{row.dismissal}</small></span>
        <span>{row.runs}</span><span>{row.balls}</span><span>{row.fours}</span><span>{row.sixes}</span>
        {hasImpact(innings) && <span className={impactClass(row.impact)}>{row.impact != null ? signed(row.impact) : ''}</span>}
      </div>
    ))}
    <div className="section-label">{innings.bowling_team} bowling</div>
    {innings.bowling.map((row) => (
      <div className="bowling-mini-row" key={row.id}>
        <span>{row.name}</span><em>{row.figures}</em><b>{row.wickets}</b>
        {row.impact != null && <i className={impactClass(row.impact)}>{signed(row.impact)}</i>}
      </div>
    ))}
  </section>
);

const BattingBreakdownScreen = ({ data, innings, searchParams, setSearchParams }) => {
  const lens = searchParams.get('batLens') || 'bowler';
  const openId = searchParams.get('openBatter');

  return (
    <main>
      <ScreenHeader
        kicker={`${innings.batting_team} innings - Scorecard`}
        title={<>{innings.batting_team} <span style={{ color: getAccent(data, innings.batting_team) }}>{innings.score.runs}/{innings.score.wickets}</span></>}
        subtitle="Tap a batter for their ball-by-ball breakdown"
      />
      <div className="scorecard-row-header batting"><span>BATTER</span><span>R</span><span>B</span><span>SR</span></div>
      <div className="accordion-list">
        {innings.batting.map((player) => (
          <BatterAccordionRow
            key={player.id}
            data={data}
            innings={innings}
            player={player}
            open={openId === player.id}
            lens={lens}
            onToggle={() => setParam(searchParams, setSearchParams, { openBatter: openId === player.id ? null : player.id, target: null })}
            onLens={(nextLens) => setParam(searchParams, setSearchParams, { batLens: nextLens })}
            onCrossLink={(bowlerId) => setParam(searchParams, setSearchParams, {
              screen: 'bowling',
              innings: innings.innings,
              openBowler: bowlerId,
              bowlLens: 'batter',
              target: `bowler-${innings.innings}-${bowlerId}`,
            })}
          />
        ))}
      </div>
    </main>
  );
};

const BowlingBreakdownScreen = ({ data, innings, searchParams, setSearchParams }) => {
  const lens = searchParams.get('bowlLens') || 'batter';
  const openId = searchParams.get('openBowler');

  return (
    <main>
      <ScreenHeader
        kicker={`${innings.bowling_team} bowling`}
        title={<>{innings.bowling_team} <span style={{ color: getAccent(data, innings.bowling_team) }}>bowling</span></>}
        subtitle="Tap a bowler for their ball-by-ball breakdown"
      />
      <div className="scorecard-row-header bowling"><span>BOWLER</span><span>O</span><span>R</span><span>W</span></div>
      <div className="accordion-list">
        {innings.bowling.map((player) => (
          <BowlerAccordionRow
            key={player.id}
            data={data}
            innings={innings}
            player={player}
            open={openId === player.id}
            lens={lens}
            onToggle={() => setParam(searchParams, setSearchParams, { openBowler: openId === player.id ? null : player.id, target: null })}
            onLens={(nextLens) => setParam(searchParams, setSearchParams, { bowlLens: nextLens })}
            onCrossLink={(batterId) => setParam(searchParams, setSearchParams, {
              screen: 'batting',
              innings: innings.innings,
              openBatter: batterId,
              batLens: 'bowler',
              target: `batter-${innings.innings}-${batterId}`,
            })}
          />
        ))}
      </div>
    </main>
  );
};

const ScreenHeader = ({ kicker, title, subtitle }) => (
  <section className="screen-header">
    <div className="scorecard-kicker">{kicker}</div>
    <h2>{title}</h2>
    <p>{subtitle}</p>
  </section>
);

const BatterAccordionRow = ({ data, innings, player, open, lens, onToggle, onLens, onCrossLink }) => (
  <article id={`batter-${innings.innings}-${player.id}`} className="scorecard-accordion-row">
    <button type="button" className="accordion-trigger batting" onClick={onToggle}>
      <span className="player-name">{player.name}<small>{player.dismissal}</small></span>
      <b>{player.runs}</b><em>{player.balls}</em><strong>{player.strike_rate}</strong>
      <span className={`row-chevron ${open ? 'open' : ''}`} aria-hidden="true" />
    </button>
    {open && (
      <div className="accordion-panel">
        <SegmentedControl options={BAT_LENSES} value={lens} onChange={onLens} />
        {lens === 'bowler' && <BatVsBowler player={player} innings={innings} onCrossLink={onCrossLink} />}
        {lens === 'phase' && <PhaseCards rows={player.breakdowns.phase} batting />}
        {lens === 'pace' && <SplitCards rows={player.breakdowns.pace_spin} batting />}
        {lens === 'zones' && <WagonSpokes rows={player.breakdowns.zones} color={getAccent(data, innings.batting_team)} />}
        {lens === 'grid' && <LineLengthHeatmap rows={player.breakdowns.line_length} batting />}
      </div>
    )}
  </article>
);

const BowlerAccordionRow = ({ data, innings, player, open, lens, onToggle, onLens, onCrossLink }) => (
  <article id={`bowler-${innings.innings}-${player.id}`} className="scorecard-accordion-row">
    <button type="button" className="accordion-trigger bowling" onClick={onToggle}>
      <span className="player-name">{player.name}<small>{player.style || 'Bowler'}</small></span>
      <b>{player.overs}</b><em>{player.runs}</em><strong>{player.wickets}</strong>
      <span className={`row-chevron ${open ? 'open' : ''}`} aria-hidden="true" />
    </button>
    {open && (
      <div className="accordion-panel">
        <SegmentedControl options={BOWL_LENSES} value={lens} onChange={onLens} />
        {lens === 'batter' && <BowlVsBatter player={player} innings={innings} onCrossLink={onCrossLink} />}
        {lens === 'phase' && <PhaseCards rows={player.breakdowns.phase} />}
        {lens === 'hand' && <HandCards rows={player.breakdowns.hand} />}
        {lens === 'zones' && <WagonSpokes rows={player.breakdowns.zones} color="#e5484d" bowling />}
        {lens === 'grid' && <LineLengthHeatmap rows={player.breakdowns.line_length} />}
      </div>
    )}
  </article>
);

const SegmentedControl = ({ options, value, onChange }) => (
  <div className="scorecard-segmented">
    {options.map((option) => (
      <button key={option.key} type="button" className={value === option.key ? 'active' : ''} onClick={() => onChange(option.key)}>
        {option.label}
      </button>
    ))}
  </div>
);

const BatVsBowler = ({ player, innings, onCrossLink }) => (
  <div>
    <div className="panel-title">{player.name} vs each bowler</div>
    {player.breakdowns.vs_bowler.rows.map((row) => {
      const linkable = innings.bowling.some((item) => item.id === row.id);
      return (
        <div className="matchup-bar-row" key={row.id} onClick={() => linkable && onCrossLink(row.id)} role={linkable ? 'button' : undefined}>
          <div className="bar-row-top">
            <span>{row.name}{linkable && <button type="button" className="cross-link"> &gt; BOWLING</button>}</span>
            <b>{row.runs} <small>({row.balls})</small></b>
          </div>
          <MetricBar pct={row.bar_pct} color="linear-gradient(90deg,#f0b429,#f7d774)" />
          <div className="metric-trail"><strong>SR {row.sr}</strong><span>{row.fours}x4</span><span>{row.sixes}x6</span><span>{row.dots} dots</span></div>
        </div>
      );
    })}
  </div>
);

const BowlVsBatter = ({ player, innings, onCrossLink }) => (
  <div>
    <div className="panel-title">{player.name} vs each batter</div>
    {player.breakdowns.vs_batter.rows.map((row) => {
      const linkable = innings.batting.some((item) => item.id === row.id);
      return (
        <div className="matchup-bar-row" key={row.id} onClick={() => linkable && onCrossLink(row.id)} role={linkable ? 'button' : undefined}>
          <div className="bar-row-top">
            <span>{row.name}{linkable && <button type="button" className="cross-link gold-link"> &gt; BATTING</button>}</span>
            <b>{row.runs} <small>({row.balls})</small></b>
          </div>
          <MetricBar pct={row.bar_pct} color={row.bar_color} />
          <div className="metric-trail"><strong>Econ {row.econ}</strong><span>{row.fours}x4</span><span>{row.sixes}x6</span><span>{row.wkts} wkt</span><span>{row.dots} dots</span></div>
        </div>
      );
    })}
  </div>
);

const PhaseCards = ({ rows, batting = false }) => {
  if (!rows?.available) return <EmptyState text={rows?.empty || 'Not enough balls for this split.'} />;
  return (
    <div className="split-grid three">
      {rows.rows.map((row) => (
        <div className="split-card" key={row.key}>
          <span>{row.label}</span>
          <small>ov {row.overs}</small>
          <b className={batting ? 'gold-value' : ''}>{batting ? row.runs : row.econ}</b>
          <em>{batting ? `${row.balls}b - SR ${row.sr}` : `${row.runs}r - ${row.wkts}w`}</em>
        </div>
      ))}
    </div>
  );
};

const SplitCards = ({ rows }) => {
  if (!rows?.available) return <EmptyState text={rows?.empty || 'Not enough balls for this split.'} />;
  return (
    <div className="split-grid two">
      {rows.rows.map((row) => (
        <div className="split-card" key={row.key}>
          <span>{row.label}</span>
          <b>{row.runs} <small>off {row.balls}</small></b>
          <strong>SR {row.sr}</strong>
          <em>{row.boundary_runs} boundary runs</em>
        </div>
      ))}
    </div>
  );
};

const HandCards = ({ rows }) => {
  if (!rows?.available) return <EmptyState text={rows?.empty || 'Not enough balls for this split.'} />;
  return (
    <div className="split-grid two">
      {rows.rows.map((row) => (
        <div className="split-card" key={row.key}>
          <span>{row.label}</span>
          <b>{row.econ} <small>econ</small></b>
          <em>{row.runs}r off {row.balls} - {row.wkts}w</em>
        </div>
      ))}
    </div>
  );
};

const WagonSpokes = ({ rows, color, bowling = false }) => {
  if (!rows?.available) return <EmptyState text={rows?.empty || 'No wagon-wheel data.'} />;
  const spokes = buildSpokes(rows.rows);
  return (
    <div className="wagon-wrap">
      {/* Scales with the card (was a fixed 240px, which drew its 3.6-unit zone labels at ~8.6px). */}
      <svg viewBox="0 0 100 100" width="100%" style={{ maxWidth: 320 }}>
        <circle cx="50" cy="52" r="44" fill={bowling ? 'rgba(91,141,239,0.04)' : 'rgba(240,180,41,0.04)'} />
        <circle cx="50" cy="52" r="22" />
        {spokes.map((zone) => (
          <g key={zone.zone}>
            <line x1="50" y1="52" x2={zone.x2} y2={zone.y2} stroke={color} />
            <circle cx={zone.x2} cy={zone.y2} r="2.4" fill={color} />
            <text x={zone.lx} y={zone.ly}>{zone.label}</text>
          </g>
        ))}
        <circle cx="50" cy="52" r="3" className="center" />
      </svg>
      <p>{bowling ? 'Runs conceded by zone' : 'Runs by scoring zone'} - from wagon_zone</p>
    </div>
  );
};

const buildSpokes = (zones) => {
  const maxRuns = Math.max(...zones.map((zone) => zone.runs), 1);
  return zones.map((zone) => {
    const radius = 12 + (zone.runs / maxRuns) * (40 - 12);
    const radians = zone.angle * Math.PI / 180;
    const labelRadius = radius + 7;
    return {
      ...zone,
      x2: (50 + radius * Math.cos(radians)).toFixed(1),
      y2: (52 - radius * Math.sin(radians)).toFixed(1),
      lx: (50 + labelRadius * Math.cos(radians)).toFixed(1),
      ly: (52 - labelRadius * Math.sin(radians) + 1).toFixed(1),
    };
  });
};

const LineLengthHeatmap = ({ rows, batting = false }) => {
  if (!rows?.available) return <EmptyState text={rows?.empty || 'Line & length grid needs more tracked balls.'} />;
  const axes = rows.rows || { lines: [], lengths: [] };
  const cellMap = new Map((rows.cells || []).map((cell) => [`${cell.length}-${cell.line}`, cell]));
  return (
    <div className="heatmap-wrap">
      <div className="panel-title">{batting ? 'Strike rate' : 'Economy'} by line and length</div>
      <div className="heatmap-grid" style={{ gridTemplateColumns: `46px repeat(${axes.lines.length}, 1fr)` }}>
        <span />
        {axes.lines.map((line) => <span className="axis-label" key={line.key}>{line.label}</span>)}
        {axes.lengths.map((length) => (
          <React.Fragment key={length.key}>
            <span className="axis-label row">{length.label}</span>
            {axes.lines.map((line) => {
              const cell = cellMap.get(`${length.key}-${line.key}`);
              return (
                <span
                  key={`${length.key}-${line.key}`}
                  className="heat-cell"
                  style={{ background: cell ? heatColor(cell.metric, batting) : 'rgba(255,255,255,0.04)', color: cell && ((batting && cell.metric >= 170) || (!batting && cell.metric <= 6)) ? '#0a0c11' : undefined }}
                >
                  {cell?.balls ? cell.metric : ''}
                </span>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className={`heat-legend ${batting ? 'batting' : 'bowling'}`}><span>{batting ? 'Low' : 'Economical'}</span><i /><span>{batting ? 'High SR' : 'Expensive'}</span></div>
    </div>
  );
};

const heatColor = (metric, batting) => {
  if (batting) {
    if (metric >= 170) return 'rgba(182,242,74,0.6)';
    if (metric >= 120) return 'rgba(240,180,41,0.45)';
    return 'rgba(91,141,239,0.35)';
  }
  if (metric <= 6) return 'rgba(182,242,74,0.6)';
  if (metric <= 9) return 'rgba(240,180,41,0.45)';
  return 'rgba(229,72,77,0.45)';
};

const MetricBar = ({ pct, color }) => (
  <div className="metric-bar"><span style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, background: color }} /></div>
);

const EmptyState = ({ text }) => <div className="scorecard-empty">{text}</div>;

export default MatchScorecardPage;
